import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { useTimetable } from '../../hooks/useTimetable';
import { useActiveMembership } from '../../hooks/usePayments';
import AppHeader from '../../components/common/AppHeader';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import { ClosedBanner, ClassCard } from '../../components/classes';
import { useTranslation } from 'react-i18next';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const SHORT_DAY_I18N_KEYS = [
  'classes.dayMon', 'classes.dayTue', 'classes.dayWed', 'classes.dayThu',
  'classes.dayFri', 'classes.daySat', 'classes.daySun',
];

function getWeekDates(): { key: string; shortKey: string; date: number; isToday: boolean; fullDate: string }[] {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      key: DAY_KEYS[i],
      shortKey: SHORT_DAY_I18N_KEYS[i],
      date: d.getDate(),
      isToday:
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate(),
      fullDate: d.toISOString().split('T')[0], // YYYY-MM-DD
    };
  });
}

export default function ClassesScreen() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const weekDates = useMemo(getWeekDates, []);
  const todayKey = weekDates.find((d) => d.isToday)?.key || 'mon';
  const [selectedDay, setSelectedDay] = useState(todayKey);

  const selectedDate = weekDates.find((d) => d.key === selectedDay)?.fullDate;
  const { data: timetable, isLoading } = useTimetable({ day: selectedDay, target_date: selectedDate });
  const { data: activeMembership } = useActiveMembership();

  // Get time and day restrictions from first active member (main user or selected dependent)
  const firstMember = activeMembership?.members?.[0];
  const planStartTime = firstMember?.membership?.plan?.allowed_start_time;
  const planEndTime = firstMember?.membership?.plan?.allowed_end_time;
  const planAllowedDays = firstMember?.membership?.plan?.allowed_days_of_week;
  const hasTimeRestrictions = !!(planStartTime || planEndTime);
  const hasDayRestrictions = !!(planAllowedDays && planAllowedDays.length > 0);
  const isSelectedDayAllowed = hasDayRestrictions ? planAllowedDays?.includes(selectedDay as import('../../api/payments').DayOfWeek) : true;

  // Helper to check if class time is within plan restrictions
  const isClassWithinAllowedHours = (classStartTime: string): boolean => {
    if (!hasTimeRestrictions) return true;
    if (!classStartTime) return true;

    const classTime = classStartTime.slice(0, 5); // Get HH:MM format

    // Handle only start time restriction
    if (planStartTime && !planEndTime) {
      return classTime >= planStartTime.slice(0, 5);
    }
    // Handle only end time restriction
    if (!planStartTime && planEndTime) {
      return classTime <= planEndTime.slice(0, 5);
    }
    // Handle both start and end time
    if (planStartTime && planEndTime) {
      const start = planStartTime.slice(0, 5);
      const end = planEndTime.slice(0, 5);
      // Normal range (e.g., 06:00-18:00)
      if (start <= end) {
        return classTime >= start && classTime <= end;
      }
      // Overnight range (e.g., 22:00-06:00)
      return classTime >= start || classTime <= end;
    }
    return true;
  };

  const entries = (timetable || []).sort((a: any, b: any) => {
    const aTime = a.override_start || a.start_time || '';
    const bTime = b.override_start || b.start_time || '';
    return aTime.localeCompare(bTime);
  });

  // Check if all entries are closed for the day
  const allClosed = entries.length > 0 && entries.every((e: any) => e.is_closed);
  const closedReason = allClosed && entries[0]?.closed_reason ? entries[0].closed_reason : null;
  const isPublicHoliday = allClosed && entries[0]?.is_public_holiday;

  // Check if day is restricted for the selected day
  const isDayRestricted = hasDayRestrictions && !isSelectedDayAllowed;


  return (
    <View style={[styles.screen, { backgroundColor: colors.bg }]}>
      <AppHeader />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, responsiveContentStyle]}
        showsVerticalScrollIndicator={false}
      >
        <Text style={[styles.title, { color: colors.text }]}>{t('classes.timetable')}</Text>

        {/* Date Scroller */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.dateRow}
        >
          {weekDates.map((d) => {
            const isActive = selectedDay === d.key;
            return (
              <TouchableOpacity
                key={d.key}
                onPress={() => setSelectedDay(d.key)}
                style={[
                  styles.dateChip,
                  {
                    backgroundColor: isActive ? colors.accent : colors.card,
                    borderColor: isActive ? colors.accent : colors.border,
                  },
                ]}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.dateChipDay,
                    {
                      color: isActive ? colors.accentText : colors.textSecondary,
                    },
                  ]}
                >
                  {t(d.shortKey)}
                </Text>
                <Text
                  style={[
                    styles.dateChipDate,
                    {
                      color: isActive ? colors.accentText : colors.textSecondary,
                    },
                  ]}
                >
                  {d.date}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {/* Time/Day Restriction Banner */}
        {(hasTimeRestrictions || hasDayRestrictions) && (
          <View style={[styles.timeRestrictionBanner, { backgroundColor: colors.warning + '20', borderColor: colors.warning }]}>
            <Icon name="clock-outline" size={16} color={colors.warning} />
            <Text style={[styles.timeRestrictionText, { color: colors.textSecondary }]}>
              {isDayRestricted
                ? t('classes.dayNotAllowed', { defaultValue: 'Your plan does not allow classes on this day' })
                : t('classes.timeRestriction', { start: planStartTime?.slice(0, 5) || '--:--', end: planEndTime?.slice(0, 5) || '--:--', defaultValue: `Your plan allows classes ${planStartTime?.slice(0, 5) || '--:--'} - ${planEndTime?.slice(0, 5) || '--:--'}` })}
            </Text>
          </View>
        )}

        {/* Class List */}
        {isLoading ? (
          <LoadingIndicator message={t('classes.loadingClasses')} />
        ) : entries.length === 0 ? (
          <View style={styles.emptyBox}>
            <Text style={[styles.emptyText, { color: colors.textTertiary }]}>
              {t('classes.noClassesScheduled')}
            </Text>
          </View>
        ) : (
          <View style={styles.classList}>
            {/* Full-day closed banner */}
            {allClosed && (
              <ClosedBanner isPublicHoliday={isPublicHoliday} reason={closedReason} isToday={selectedDay === todayKey} />
            )}

            {isDayRestricted ? (
              <View style={styles.emptyBox}>
                <Text style={[styles.emptyText, { color: colors.textTertiary }]}>
                  {t('classes.dayNotAllowed', { defaultValue: 'Your membership plan does not allow classes on this day' })}
                </Text>
              </View>
            ) : (
              entries.map((entry: any, i: number) => {
                const isAllowed = isClassWithinAllowedHours(entry.start_time);
                return (
                  <View key={entry.schedule_id || i} style={{ opacity: isAllowed ? 1 : 0.5 }}>
                    <ClassCard
                      entry={entry}
                      showBadges={true}
                    />
                    {!isAllowed && hasTimeRestrictions && (
                      <View style={[styles.restrictedBadge, { backgroundColor: colors.warning + '15' }]}>
                        <Icon name="clock-alert-outline" size={12} color={colors.warning} />
                        <Text style={[styles.restrictedText, { color: colors.warning }]}>
                          {t('classes.outsidePlanHours', { defaultValue: 'Outside plan hours' })}
                        </Text>
                      </View>
                    )}
                  </View>
                );
              })
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  scroll: { flex: 1 },
  content: { padding: 24, paddingBottom: 120, gap: 24 },

  title: {
    fontSize: 24,
    fontWeight: '700',
  },

  // Date scroller
  dateRow: {
    gap: 12,
    paddingBottom: 4,
  },
  dateChip: {
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: 'center',
    minWidth: 60,
    borderWidth: 1,
  },
  dateChipDay: {
    fontSize: 11,
    fontWeight: '700',
  },
  dateChipDate: {
    fontSize: 18,
    fontWeight: '700',
  },

  // Class list
  classList: {
    gap: 12,
  },
  classCard: {
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
  },
  classCardLeft: {
    flex: 1,
    gap: 4,
  },
  classTime: {
    fontSize: 13,
  },
  classTitle: {
    fontSize: 15,
    fontWeight: '700',
  },
  classMeta: {
    fontSize: 12,
  },

  emptyBox: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
  },
  // Time restriction styles
  timeRestrictionBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  timeRestrictionText: {
    fontSize: 12,
    flex: 1,
  },
  restrictedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  restrictedText: {
    fontSize: 11,
    fontWeight: '600',
  },
});
